import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '../../../i18n/LanguageContext';
import { courseContentService } from '@/modules/lcms/content-courses/services/courseContentService';

interface ImportCourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

export const ImportCourseDialog: React.FC<ImportCourseDialogProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const { t } = useLanguage();
  const [jsonContent, setJsonContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setJsonContent('');
      setError(null);
    }
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonContent(e.target.value);
    setFile(null);
    setError(null);
  };

  const handleImport = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (file) {
        await courseContentService.importCourse({ file });
      } else if (jsonContent) {
        // Create a file from the JSON content
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const jsonFile = new File([blob], 'course.json', { type: 'application/json' });
        await courseContentService.importCourse({ file: jsonFile });
      } else {
        setError(t('course-content.import.error.noContent'));
        return;
      }

      onImportSuccess();
      onClose();
    } catch (err) {
      setError(t('course-content.import.error.failed'));
      console.error('Failed to import course:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('course-content.import.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('course-content.import.uploadFile')}
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-indigo-50 file:text-indigo-700
                hover:file:bg-indigo-100"
            />
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-white text-sm text-gray-500">
                {t('course-content.import.or')}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('course-content.import.pasteJson')}
            </label>
            <Textarea
              value={jsonContent}
              onChange={handleJsonChange}
              placeholder={t('course-content.import.jsonPlaceholder')}
              className="min-h-[200px] font-mono text-sm"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleImport} disabled={isLoading}>
            {isLoading ? t('common.importing') : t('common.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 